<template>
  <CustomModal
    class="change-commission-level-smart-boost"
    :is-modal-open="isChangeCommissionLevelOpen"
    @toggleModal="closeChangeCommissionLevelModal"
    @onClickAway="closeChangeCommissionLevelModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[636px] tw:md:w-[636px] tw:md:rounded-md tw:rounded-none"
  >
    <template #modalHeader>
      <p class="tw:text-xl tw:max-w-[90%] tw:font-bold">
        {{ $t(headerText) }}
      </p>
    </template>
    <div class="tw:flex tw:flex-col tw:w-full tw:gap-y-6">
      <i18n-t
        :keypath="
          isDeleting
            ? 'rc_smartboost_cancel_subtext'
            : isCommissionIncrease
              ? 'rc_smartboost_commission_change_high'
              : 'rc_smartboost_commission_change_low'
        "
        tag="p"
      >
        <template #CheckindDate>
          <span class="tw:font-bold">{{ editedBoost.formattedDate }}</span>
        </template>
      </i18n-t>
      <div class="tw:flex tw:gap-x-3 tw:items-center tw:justify-center">
        <i18n-t class="tw:flex tw:gap-x-3 tw:items-center" tag="p" keypath="rc_opportunities_commission_change">
          <template #FromPercentage>
            <span class="tw:text-[32px] tw:font-bold tw:leading-none">
              {{ formatPercentage(language, editedBoost.currentCommissionLevel, 0, 0) }}
            </span>
          </template>
          <template #ToPercentage>
            <span
              class="tw:text-[32px] tw:font-bold tw:leading-none"
              :class="{ 'tw:text-red-700': !isCommissionIncrease }"
            >
              {{ formatPercentage(language, newCommissionLevel, 0, 0) }}
            </span>
          </template>
        </i18n-t>
      </div>
      <div>
        <p
          v-html="
            isCommissionIncrease
              ? $t('rc_opportunities_commission_change_info')
              : $t('rc_opportunities_commission_decrease_info')
          "
        />
        <ul class="tw:my-6 tw:list-disc tw:pl-6">
          <li v-for="(point, index) in points" :key="`point_${index}`">{{ $t(point) }}</li>
        </ul>
        <p v-if="!isCommissionIncrease" class="tw:mb-6">
          {{ $t('rc_opportunities_commission_decrease_confirm') }}
        </p>
        <p class="tw:text-sm">
          {{ $t('rc_smartboost_commission_change_footnote') }}
        </p>
      </div>
    </div>

    <template #modalFooter>
      <div
        class="tw:flex tw:w-full tw:items-start tw:md:items-center tw:md:flex-row tw:flex-col tw:justify-end tw:gap-4"
      >
        <GhostButton
          class="tw:w-full tw:md:w-auto"
          :dataTestId="'smart-boost-change-modal-back-button'"
          @click="closeChangeCommissionLevelModal"
        >
          {{ $t('rc_smartboost_back_cta') }}
        </GhostButton>
        <MainButton
          class="tw:w-full tw:md:w-auto"
          :dataTestId="
            isDeleting ? 'smart-boost-delete-modal-confirm-button' : 'smart-boost-change-modal-confirm-button'
          "
          @click="confirmAction"
          :disabled="isLoading"
        >
          <span v-show="!isLoading" class="tw:text-nowrap">{{ $t('rc_opportunities_cta_confirm') }}</span>
          <span v-show="isLoading"> <SmallDotLoader color="#fff" /></span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, ref } from 'vue'

import CustomModal from '@/components/base/CustomModal.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'

import { formatPercentage } from '@/utils/Utility.ts'
import { useSmartBoost } from './useSmartBoost'
import { useDeleteBoostMutation, useEditBoostMutation } from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'

import useToastNotifications from '@/components/notifications/useToastNotifications'
import appAnalytics from '@/utils/tracking'
import { useSessionValues } from '@/composables/useSessionValues'

const { isChangeCommissionLevelOpen, closeChangeCommissionLevelModal, selectedCommission } = useSmartBoost()
const { displayNotification, showSaveError } = useToastNotifications()
const { editBoost } = useEditBoostMutation()
const { deleteBoost } = useDeleteBoostMutation()

const props = defineProps({
  editedBoost: Object,
  isDeleting: Boolean,
})

const { language } = useSessionValues()
const isLoading = ref(false)

const headerText = computed(() => {
  if (props.isDeleting) {
    return 'rc_smartboost_cancel_header'
  }
  return isCommissionIncrease.value
    ? 'rc_opportunities_commission_increase_header'
    : 'rc_opportunities_commission_decrease_header'
})

// Computed property for the new commission level
const newCommissionLevel = computed(() => {
  return props.isDeleting ? props.editedBoost?.baseCommissionLevel : selectedCommission.value
})

// Computed property to determine if commission is increasing
const isCommissionIncrease = computed(() => {
  if (!props.editedBoost) return false

  if (props.isDeleting) {
    return props.editedBoost.currentCommissionLevel < props.editedBoost.baseCommissionLevel
  } else {
    return selectedCommission.value > props.editedBoost.currentCommissionLevel
  }
})

const points = computed(() => {
  return isCommissionIncrease.value
    ? ['rc_opportunities_commission_benefit_2', 'rc_opportunities_commission_benefit_3']
    : [
        'rc_smartboost_commission_decrease_info_1',
        'rc_opportunities_commission_decrease_info_2',
        'rc_opportunities_commission_decrease_info_3',
      ]
})

const confirmCommissionChange = () => {
  editBoost(
    {
      accommodationId: selectedAccommodationId.value,
      boostId: props.editedBoost.id,
      body: {
        boostCommissionLevel: selectedCommission.value,
        interval: {
          startDate: props.editedBoost.startDate,
          endDate: props.editedBoost.endDate,
        },
      },
    },
    {
      onSuccess: () => {
        isLoading.value = false
        appAnalytics.track(appAnalytics.events.RC_SMART_BOOST_UPDATED, {
          item_id: selectedAccommodationId.value,
        })
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_cpa_commission_change_success',
          isTranslationKey: true,
          type: isCommissionIncrease.value ? 'success' : 'info',
        })
        closeChangeCommissionLevelModal()
        selectedCommission.value = null
      },
      onError: () => {
        isLoading.value = false
        showSaveError()
      },
    }
  )
}

const confirmDeleting = () => {
  deleteBoost(
    {
      accommodationId: selectedAccommodationId.value,
      boostId: props.editedBoost.id,
    },
    {
      onSuccess: () => {
        appAnalytics.track(appAnalytics.events.RC_SMART_BOOST_DELETED, {
          item_id: selectedAccommodationId.value,
        })
        isLoading.value = false
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_smartboost_canceled_success',
          isTranslationKey: true,
          type: 'info',
        })
        closeChangeCommissionLevelModal()
        selectedCommission.value = null
      },
      onError: () => {
        isLoading.value = false
        showSaveError()
      },
    }
  )
}

const confirmAction = () => {
  isLoading.value = true
  if (props.isDeleting) {
    confirmDeleting()
  } else {
    confirmCommissionChange()
  }
}
</script>
